import { ChangeEvent, MouseEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Contract, ContractId } from "shared/models";
import { CheckField, RadioField } from "ui/shared/controls";
import { Loading } from "ui/shared/loading";
import { BillingStrings, getExpiryMessage, makeContractServicesString } from "ui/contracts/contracts";
import { LoadingSpinner } from "ui/shared/LoadingSpinner";

require('./OrgContractField.css');

export type Props = {
    contracts: Record<ContractId, Contract>;
    contractIds: ContractId[];
    id: string;
    name: string;
    value: ContractId | Record<ContractId, true> | null;
    disabled?: boolean;
    readOnly?: boolean;
    label: string;
    replace?: boolean;
    className?: string;
    loading?: boolean;
    onChange(e: ChangeEvent<HTMLInputElement>): void;
    onConfigure?(): void;
};

export const OrgContractField = ({ id, name, label, replace = false, contractIds, contracts, value, disabled, readOnly = false, loading, onConfigure, onChange }: Props) => {
    const { t } = useTranslation();

    const firstSelectedContractId =
        typeof value === 'string' || !value
            ? value
            : value && Object.keys(value).length
                ? Object.keys(value)[0]
                : null;

    const canUnassignContract =
        (typeof value === 'object' && Object.keys(value || {}).length) ||
        !contracts[firstSelectedContractId!]?.locked;

    const handleClickListItem = useCallback((e: React.MouseEvent<HTMLLIElement>) => {
        const { currentTarget, target } = e;
        const input = currentTarget.querySelector('input');
        if (input && target !== input) {
            input.click();
        }
    }, []);

    return (
        <div
            id={ id }
            className="org-contract-field field"
        >
            <label className={ disabled ? 'disabled' : '' }>
                { label }
            </label>
            { onConfigure &&
                <button className="ui tiny button" type="button" disabled={ disabled } onClick={ onConfigure }>
                    { t('COMMAND.CONFIGURE') }
                </button>
            }
            <ul>
                { loading &&
                    <li className="_loading">
                        <LoadingSpinner size="tiny"/>
                        <Loading/>
                    </li>
                }
                { !replace && !loading &&
                    <li
                        id={ `contract-item-none` }
                        className={ `contract-item ${disabled ? 'disabled' : ''}` }
                        onClick={ handleClickListItem }
                    >
                        <RadioField
                            id="contract-none"
                            input={ {
                                name: name,
                                value: !value || (typeof value === 'object' && !Object.keys(value).length) ? 'none' : '',
                                onChange: onChange,
                                disabled: disabled || !canUnassignContract,
                                readOnly: readOnly,
                            } }
                            myValue="none"
                        />
                        <span className="services">
                            { t('LABEL.NONE') }
                        </span>
                    </li>
                }
                { contractIds?.map(contractId =>
                    <OrgContractFieldItem
                        key={ contractId }
                        name={ name }
                        contract={ contracts[contractId] }
                        checked={ (!value || typeof value === 'string') ? value === contractId : !!value[contractId] }
                        replace={ replace }
                        onClick={ handleClickListItem }
                        onChange={ onChange }
                        disabled={ disabled || !canUnassignContract }
                        readOnly={ readOnly }
                    />
                ) }
            </ul>
        </div>
    );
};


export type ItemProps = {
    name: string;
    contract: Contract;
    checked: boolean;
    disabled: boolean;
    readOnly: boolean;
    replace: boolean;
    onChange(e: ChangeEvent<HTMLInputElement>): void;
    onClick(e: MouseEvent<HTMLElement>): void;
};

const OrgContractFieldItem = ({ name, contract, checked, disabled, readOnly, replace, onClick, onChange }: ItemProps) => {
    const { date, message: expiryMessage, isExpired } = getExpiryMessage(contract.expiryDate, false);

    const myDisabled = disabled || (!replace && (contract.locked || isExpired));

    return (
        <li
            id={ `contract-item-${contract.id}` }
            data-id={ contract.id }
            className={ `contract-item ${myDisabled ? 'disabled' : ''}` }
            onClick={ onClick }
        >
            { replace
                ? <CheckField
                    id={ `replace-contract-${contract.id}` }
                    input={ {
                        type: 'checkbox',
                        name: `${name}.${contract.id}`,
                        'data-id': contract.id,
                        value: checked,
                        readOnly,
                        onChange,
                    } }
                    disabled={ myDisabled }
                />
                : <RadioField
                    id={ `contract-${contract.id}` }
                    input={ {
                        name: name,
                        'data-id': contract.id,
                        value: checked ? contract.id : '',
                        onChange: onChange,
                        disabled: myDisabled,
                        readOnly: readOnly,
                    } }
                    myValue={ contract.id }
                />
            }
            <span className="_services">
                { contract.locked &&
                    <i
                        className="grey lock icon"
                        title="Locked"
                    ></i>
                }
                { makeContractServicesString(contract.services) }
            </span>
            <span className="_status">
                { expiryMessage }
                { isExpired && (
                    <i
                        className="red warning circle icon contract-expired-icon"
                        title={ BillingStrings.EXPIRED }
                    ></i>
                ) }
            </span>
            <span className="_price">
                { `$${contract.price.toFixed(2)} ${contract.currency.toUpperCase()} ${BillingStrings.PER_MONTH}` }
            </span>
            { date &&
                <span className="_expiry">
                    { date }
                </span>
            }
        </li>
    );
};
